import type { IconName } from '@mylighthouse/prism-ember/types/components/prism/icon';
import type { Option } from '@mylighthouse/prism-ember/types/components/prism/select-input';
import { tKey } from 'ember-intl';
import type { Platform } from 'frontend-utils/utils/constants/constant-values';
import { PLATFORMS } from 'frontend-utils/utils/constants/constant-values';
import type { CountryCode } from 'frontend-utils/utils/countries';
import type {
  MembershipType,
  RateParam,
} from 'lumen/utils/constants/general-constants';
import {
  MEMBERSHIPTYPES,
  RATE_PARAMS,
} from 'lumen/utils/constants/general-constants';

export type SelectInputOptionWithId<T> = Option & { id: T };

export const LATEST = 'latest';
export const BENCHMARK = 'benchmark';
export const DEFAULT_LOS = -1;
export const DEFAULT_POS = '';
export const HISTORICAL_POINTS_AMOUNT = 8;

export const DATE_AGGREGATION_WEEK = 'week' as const;
export const DATE_AGGREGATION_MONTH = 'month' as const;
export type DateAggregation =
  | typeof DATE_AGGREGATION_WEEK
  | typeof DATE_AGGREGATION_MONTH;

export const PARITY_FILTER = {
  LOSS: 'loss',
  MEET: 'meet',
  WIN: 'win',
} as const;

export type ParityFilter = (typeof PARITY_FILTER)[keyof typeof PARITY_FILTER];
export type ParityFilterOrBenchmark = ParityFilter | typeof BENCHMARK;

export const PARITY_FILTER_TRANSLATION_KEYS = {
  [PARITY_FILTER.LOSS]: tKey('parity_analytics.loss'),
  [PARITY_FILTER.MEET]: tKey('parity_analytics.meet'),
  [PARITY_FILTER.WIN]: tKey('parity_analytics.win'),
} as const;

export const SORT_TYPES = {
  ABSOLUTE: 'absolute',
  RELATIVE: 'relative',
} as const;

// Sort types for Parity Analytics allow to switch dynamically between the types of values on a segmentation_value object
export const PA_SORT_TYPES = {
  absolute: 'value',
  relative: 'calculatedValues',
} as const;

export const PI_BREAKDOWNS = {
  BRAND_ID: 'brand_id',
  CHANNEL: 'channel',
  CHANNEL_TYPE: 'channel_type',
  MARKET_ID: 'market_id',
  PLATFORM: 'platform',
  SOURCE: 'source',
  SUBSCRIPTION_ID: 'subscription_id',
} as const;

export type Breakdown = (typeof PI_BREAKDOWNS)[keyof typeof PI_BREAKDOWNS];

export const SORTED_PI_BREAKDOWNS: Breakdown[] = [
  PI_BREAKDOWNS.MARKET_ID,
  PI_BREAKDOWNS.BRAND_ID,
  PI_BREAKDOWNS.PLATFORM,
  PI_BREAKDOWNS.SOURCE,
  PI_BREAKDOWNS.CHANNEL,
  PI_BREAKDOWNS.CHANNEL_TYPE,
];

// List of blocks which don't get merged with cache (RAW OUTPUT) + allow only one selection
export const SINGLE_SELECT_BREAKDOWNS: Breakdown[] = [
  PI_BREAKDOWNS.CHANNEL_TYPE,
];

// List of blocks which have at most 15 concurrent selections
export const LIMITED_CONCURRENT_BREAKDOWNS: Breakdown[] = [
  PI_BREAKDOWNS.CHANNEL,
  PI_BREAKDOWNS.CHANNEL_TYPE,
  PI_BREAKDOWNS.SOURCE,
];

// List of blocks we should hide when they have no results
export const BREAKDOWNS_TO_HIDE_WHEN_EMPTY: Breakdown[] = [
  PI_BREAKDOWNS.BRAND_ID,
  PI_BREAKDOWNS.MARKET_ID,
];

// Cut off to display segmentation bar value (text would overlap for smaller values)
export const BREAKDOWN_BAR_THRESHOLD = 10;

export type SortType = (typeof SORT_TYPES)[keyof typeof SORT_TYPES];

export const DIMENSIONS = {
  // Breakdowns
  MARKET: 'market',
  BRAND: 'brand',
  CHANNEL: 'channel',
  DISCOUNT: 'bucket',
  // Promotions
  MOBILE: 'mobile',
  POS: 'pos',
  LOS: 'los',
  MEMBERSHIP: 'membership',
  // Hotel
  HOTEL: 'hotel',
} as const;

export const DISCOUNT_LABELS_KEYS = {
  LESS_THAN_5: '0',
  MORE_THAN_20: '4',
};

export const DISCOUNT_LABELS = {
  '0': tKey('general.less_than.count'),
  '1': '5% - 10%',
  '2': '10% - 15%',
  '3': '15% - 20%',
  '4': tKey('general.or_more.count'),
};

export type DimensionValue = (typeof DIMENSIONS)[keyof typeof DIMENSIONS];

export const DIMENSION_TRANSLATIONS: Record<DimensionValue, string> = {
  [DIMENSIONS.MARKET]: tKey('parity_analytics.market_id'),
  [DIMENSIONS.BRAND]: tKey('parity_analytics.brand_id'),
  [DIMENSIONS.CHANNEL]: tKey('parity_analytics.channel'),
  [DIMENSIONS.DISCOUNT]: tKey('general.discount.plural'),
  [DIMENSIONS.MOBILE]: tKey('ota.platform_mobile'),
  [DIMENSIONS.POS]: tKey('general.pos'),
  [DIMENSIONS.LOS]: tKey('parity_analytics.los'),
  [DIMENSIONS.HOTEL]: tKey('general.hotel.plural'),
  [DIMENSIONS.MEMBERSHIP]: tKey('parity_strategy.membership'),
} as const;

export const STATUSES = {
  FLAGGED_EXTRANET: 'flagged_extranet',
  RESOLVED_EXTRANET: 'resolved_extranet',
  ALLOWED_EXTRANET: 'allowed',
  FLAGGED_OTA: 'flagged_ota',
  RESOLVED_OTA: 'resolved_ota',
  NON_ISSUE: 'non_issue',
  PENDING: 'pending',
  UNKNOWN: 'unknown',
} as const;

export type StatusValue = (typeof STATUSES)[keyof typeof STATUSES];

export const STATUS_TRANSLATIONS: Record<StatusValue, string> = {
  [STATUSES.FLAGGED_EXTRANET]: tKey('parity_strategy.status.flagged_extranet'),
  [STATUSES.RESOLVED_EXTRANET]: tKey(
    'parity_strategy.status.resolved_extranet',
  ),
  [STATUSES.ALLOWED_EXTRANET]: tKey('parity_strategy.status.allowed'),
  [STATUSES.FLAGGED_OTA]: tKey('parity_strategy.status.flagged_ota'),
  [STATUSES.RESOLVED_OTA]: tKey('parity_strategy.status.resolved_ota'),
  [STATUSES.NON_ISSUE]: tKey('general.status.non_issue'),
  [STATUSES.PENDING]: tKey('general.status.pending'),
  [STATUSES.UNKNOWN]: tKey('parity_strategy.status.unknown'),
} as const;

export const MOBILE_PROMOTIONS = {
  WEB: 'web',
  APP: 'app',
} as const;

// Will be removed in PI-1149
export const MEMBERSHIP_TYPES = {
  MEMBER_DUPLICATE: '100',
  COUPON_DUPLICATE: '101',
} as const;

export type Membership =
  (typeof MEMBERSHIP_TYPES)[keyof typeof MEMBERSHIP_TYPES];

export const ISSUE_TYPES = {
  UNAVAILABLE: 0,
  LOSS_SOLDOUT: 1,
  LOSS_ROOMTYPE: 2,
  LOSS_PRICE_DIFFERENCE: 3,
  MEET_SAME_PRICE: 4,
  WIN_PRICE_DIFFERENCE: 5,
  WIN_ROOMTYPE: 6,
  WIN_SOLDOUT: 7,
  LOSS_DIFFERENT_CANCELLATION: 8,
  WIN_DIFFERENT_CANCELLATION: 9,
  MOBILE: 10,
} as const;

export type IssueType = (typeof ISSUE_TYPES)[keyof typeof ISSUE_TYPES];

export const TRANSLATIONS_MOBILE_PROMOTIONS = {
  [MOBILE_PROMOTIONS.WEB]: 'rates.platform.1',
  [MOBILE_PROMOTIONS.APP]: 'rates.platform.2',
} as const;

export const TRANSLATIONS_MEMBERSHIP_TYPES: Partial<
  Record<MembershipType | Membership, string>
> = {
  [MEMBERSHIPTYPES.MEMBER_ONLY]: tKey('parity_strategy.membership.member'),
  [MEMBERSHIPTYPES.COUPON]: tKey('parity_strategy.membership.coupon'),
  [MEMBERSHIP_TYPES.MEMBER_DUPLICATE]: tKey(
    'parity_strategy.membership.member',
  ),
  [MEMBERSHIP_TYPES.COUPON_DUPLICATE]: tKey(
    'parity_strategy.membership.coupon',
  ),
} as const;

export const RATE_TYPES = {
  LOWEST: 0,
  BEST_FLEX: 1,
} as const;

export type RateType = (typeof RATE_TYPES)[keyof typeof RATE_TYPES];

export const RATE_TYPE_TRANSLATIONS: Record<RateType, string> = {
  [RATE_TYPES.LOWEST]: tKey('rates.lowest'),
  [RATE_TYPES.BEST_FLEX]: tKey('rates.bar_short'),
} as const;

export const RATE_TYPE_ICONS: Record<RateType, IconName> = {
  [RATE_TYPES.LOWEST]: 'rates-lowest',
  [RATE_TYPES.BEST_FLEX]: 'rates-best-flex',
};

export const RATE_TYPE_TO_RATE_PARAM: Record<RateType, RateParam> = {
  [RATE_TYPES.LOWEST]: RATE_PARAMS.LOWEST,
  [RATE_TYPES.BEST_FLEX]: RATE_PARAMS.BEST_FLEX,
} as const;

export const PLATFORM_PRETTY_NAMES: Record<Platform, string> = {
  [PLATFORMS.PLATFORM_UNSPECIFIED]: 'Desktop',
  [PLATFORMS.PLATFORM_DESKTOP]: 'Desktop',
  [PLATFORMS.PLATFORM_MOBILE_WEBSITE]: 'Mobile web',
  [PLATFORMS.PLATFORM_MOBILE_APP]: 'Mobile app',
};

export type OTAGroup = 'major' | 'all';

export type Percentages = {
  loss: number;
  meet: number;
  win: number;
};

export type PercentagesWithSubscriptions = Percentages & {
  subscriptions: number;
};

export type PercentagesWithTotal = Percentages & { totalNbOfShops: number };

export type CountryCodeWithDefault = CountryCode | typeof DEFAULT_POS;
